import { FormControlLabel, styled, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ColorPalette from 'common/theme/colorPalette';
import { Stack, SxProps } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid-premium';
import { HorizontalScrollableContainer } from 'common/components/container';
import { FloatingMenuItem } from 'common/components/core/FloatingMenu';
import CXDataGridManageColumnsButton from 'common/components/cx-data-grid/cx-data-grid-manage-columns-button.component';
import { CommonStyledLink } from 'common/components/links/styles/common-link.style';
import { Box, Button, Card, Switch, Typography } from 'common/components/material';
import { CardContent } from 'common/components/material/Card';
import { PersonLink } from 'common/components/person-link/person-link.component';
import axios from 'common/utils/axios';
import SkillChip from 'features/org-root/components/SkillChip';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { Skill } from 'features/skills';
import { GridColumn } from 'libs/mui/data-grid/types/data-grid-props.type';
import { CXViewer } from 'libs/toast-ui';
import _, { get, range } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import * as yup from 'yup';
import { DownvoteButton, UpvoteButton } from './components/SkillEndorsementButtons';
import { PastFeedbackDropdownMenu } from './components/SkillFeedbackCard';
import { useDrawer, useSession } from 'common/hooks';
import colorPalette from 'common/theme/colorPalette';

import { Location } from 'features/static-data';
import { BaseProfile, SearchResult } from 'features/search';
import { RectangularSkeleton } from 'common/components/loaders';
import { useFeatureAccess } from 'features/feature-access/hooks/useFeatureAccess';
import { FeaturesEnum } from 'features/feature-access/features.enum';
import { FeatureAccessEnum } from 'features/feature-access/enums/feature-access.enum';
import VerifiedSharpIcon from '@mui/icons-material/VerifiedSharp';
import {
  compileStructuredHighlights,
  Highlights,
  ProfileHighlights,
} from './components/matching-engine-person-drawer-summary';
import { useSearchParams } from 'react-router-dom';
import CircleIcon from '@mui/icons-material/Circle';
import NoResultsImage from 'common/components/image/NoResultsImage';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';

export const matchingEngineApi = axios('matching-engine');
export const CUT_OFF_DAY_COUNT = 30;
export const MATCHING_ENGINE_ROW_HEIGHT = 28;
export const MATCHING_ENGINE_COLUMN_HEIGHT = 30;

type ClickHandler = (e: React.MouseEvent<HTMLElement>) => void;

export type ManagersMetadata = {
  manager: BaseProfile;
  l2Manager?: BaseProfile;
  l3Manager?: BaseProfile;
};

export type StaticMetadata = Location;

export type Allocation = {
  id: string;
  name: string;
  criticality: string;
  sponsor: string;
  raw: object;
  date: string;
  totalContributedDays: number;
  contributions: object[];
};

type MostCriticalLastAllocation = {
  details: string;
  lastContributionDate: string;
};

export type AllocationMetadata = {
  itemsCount: number;
  mostCriticalLastAllocation: MostCriticalLastAllocation;
  highCriticalityAllocations: Allocation[];
  mediumCriticalityAllocations: Allocation[];
  lowCriticalityAllocations: Allocation[];
  unknownCriticalityAllocations?: Allocation[];
};

export type PerformanceMetadata = {
  profileId: string;
  customerRecommendationScore: number;
  peerRecommendationScore: number;
  managerRecommendationScore: number;
  recommendationComments: string | undefined;
  performanceRating: number;
  performanceReason: string | undefined;
  potentialRating: number;
  potentialReason: string | undefined;
  criticalityRating: number;
  criticalityReason: string | undefined;
  rank: number;
};

export type PersonData = {
  id: string;
  entityId: string;
  fullName: string;
  photo: string | null;
  allocationMetadata: AllocationMetadata;
  performance: PerformanceMetadata;
  skills: Skill[];
  verifiedSkills: Skill[];
  inferredSkills: Skill[];
  missingSkills: Skill[];
  personSkills?: Skill[];
  profileHighlights?: Highlights;
  feedbacks?: any[];
  missingCount: number;
  uberManagers: BaseProfile[];
  managers: ManagersMetadata;
  workLocation: StaticMetadata;
  department: StaticMetadata;
  division: StaticMetadata;
  product: StaticMetadata;
  isSkillEnriched?: boolean;
};

type GridCardSkeletonViewProps = {
  cardsCount?: number;
  gap?: string | number;
};

export const GridCardSkeletonView: React.FC<GridCardSkeletonViewProps> = ({
  cardsCount = 12,
  gap = '0.5rem',
}) => {
  return (
    <Stack direction={'column'} gap={gap}>
      {range(cardsCount).map((key) => (
        <RectangularSkeleton key={key} height="2rem" />
      ))}
    </Stack>
  );
};

export const scrollbarStyles = (theme: any) => ({
  '& .MuiDataGrid-scrollbar': {
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'transparent',
      borderRadius: '8px',
      border: '4px solid transparent',
      backgroundClip: 'content-box',
    },
  },

  '&:hover .MuiDataGrid-scrollbar': {
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.divider,
    },
  },

  '& .MuiDataGrid-scrollbar--vertical': {
    width: '8px',
  },

  '& .MuiDataGrid-scrollbar--horizontal': {
    height: '8px',
  },
});

export const faintScrollbarStyles = (theme: any) => ({
  '&::-webkit-scrollbar': {
    width: '4px',
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    border: theme.palette.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.divider,
  },
});

export const CELL_CLASSES = {
  CLICKED: 'cell-clicked',
  ACTIVITY_DETAILS: 'cell-for-activity-details',
  EDIT: 'edit-cell',
} as const;

export const MatchingEngineLegends = () => {
  const { palette } = useTheme();

  const legends = [
    { label: 'Verified', color: ColorPalette.GREEN400 },
    { label: 'Inferred', color: ColorPalette.YELLOW500 },
    { label: 'Missing', color: ColorPalette.GRAY900 },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
      {legends.map(({ label, color }) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <CircleIcon
            sx={{
              fontSize: 10,
              color,
              display: 'block',
            }}
          />
          <Typography variant="caption" color={palette.Text.Caption} sx={{ lineHeight: 1 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const SkillStatusIcon: React.FC<{
  person: SearchResult;
  skill: Skill;
  verifiedSkills: any[];
  inferredSkills: any[];
}> = ({ person, skill, verifiedSkills, inferredSkills }) => {
  const theme = useTheme();

  const verifiedSkillSet = new Set(verifiedSkills.map((skill: Skill) => skill.id));
  const inferredSkillSet = new Set(inferredSkills.map((skill: Skill) => skill.id));

  const hasSkill = verifiedSkillSet.has(skill.id);
  if (hasSkill) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <CircleIcon
          color="success"
          sx={{ fontSize: '10px !important', color: `${colorPalette.GREEN400} !important` }}
        />
      </Box>
    );
  }

  const isInferred = inferredSkillSet.has(skill.id);
  if (isInferred) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: 24,
          height: 24,
        }}
      >
        <CircleIcon
          sx={{ fontSize: '10px !important', color: `${colorPalette.YELLOW500} !important` }}
        />
      </Box>
    );
  }

  return (
    <CircleIcon
      color="error"
      sx={{ fontSize: '10px !important', color: `${colorPalette.GRAY900} !important` }}
    />
  );
};

export const getCellValue = (
  personData: any,
  attribute: string,
  skills: Skill[],
  highlights?: any,
) => {
  const l2ManagerProfile = _.nth(personData.organizationProfile.uberManagers, -2);
  const l3ManagerProfile = _.nth(personData.organizationProfile.uberManagers, -3);
  const { verifiedSkills, inferredSkills, missingSkills } = getSkillGroups(
    personData.organizationProfile.profileSkills ?? [],
    skills,
  );

  switch (attribute) {
    case PEOPLE_COMPARISON_COLUMN_FIELDS.SUMMARY:
      const structured = compileStructuredHighlights({
        highlights: highlights,
        skillGroups: {
          managerVerifiedSkills: verifiedSkills,
          inferredSkills: inferredSkills,
          missingSkills: missingSkills,
        },
      });
      return (
        <Box py={1} width="100%" whiteSpace="normal" sx={{ lineHeight: '1.2rem' }}>
          <ProfileHighlights structured={structured} />
        </Box>
      );
    case PEOPLE_COMPARISON_COLUMN_FIELDS[PEOPLE_COMPARISON_COLUMN_KEYS.LAST_ALLOCATION](
      CUT_OFF_DAY_COUNT,
    ):
      return personData.organizationProfile?.recentActivitiesCriticalityMetadata;

    case PEOPLE_COMPARISON_COLUMN_FIELDS.MANAGER:
      return personData.organizationProfile?.manager;
    case PEOPLE_COMPARISON_COLUMN_FIELDS.L2MANAGER:
      return l2ManagerProfile;
    case PEOPLE_COMPARISON_COLUMN_FIELDS.L3MANAGER:
      return l3ManagerProfile;
    case PEOPLE_COMPARISON_COLUMN_FIELDS.LOCATION:
      return personData.organizationProfile?.workLocation?.name;
    case PEOPLE_COMPARISON_COLUMN_FIELDS.DEPARTMENT:
      return personData.organizationProfile?.department?.name;
    case PEOPLE_COMPARISON_COLUMN_FIELDS.DIVISION:
      return personData.organizationProfile?.division?.name;
    case PEOPLE_COMPARISON_COLUMN_FIELDS.PRODUCT:
      return personData.organizationProfile?.product?.name;

    default:
      return '-';
  }
};

export const DrawerTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  fontWeight: 700,
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

export enum SkillStatus {
  VERIFIED = 'Confirmed',
  INFERRED = 'Inferred',
  MISSING = 'Missing',
}

export const SkillType = {
  SKILL: 'Skill',
  PRODUCT: 'Product',
  PRODUCT_FAMILY: 'Product Family',
  DOMAIN: 'Domain',
  VENUE_TYPE: 'Venue Type',
  WORKFLOW_TYPE: 'Workflow Type',
  PROTOCOL: 'Protocol',
  ASSET_CLASS: 'Asset Class',
  MARKET: 'Market',
  SUB_COMPONENT: 'Sub-Component',
  COMPONENT_TECHNOLOGY: 'Component Technology',
  COMPONENT: 'Component',
};

type SkillTypeKey = keyof typeof SkillType;

export const PEOPLE_SEARCH_PARAM = 'people';
export const CRITICAL_PEOPLE_SEARCH_PARAM = 'excludeCriticalPeople';
export const UNVERIFIED_SKILL_SEARCH_PARAM = 'excludeUnverifiedSkills';

export enum PEOPLE_COMPARISON_COLUMN_KEYS {
  CONFIRMED_SKILLS = 'CONFIRMED_SKILLS',
  INFERRED_SKILLS = 'INFERRED_SKILLS',
  MISSING_SKILLS = 'MISSING_SKILLS',
  MANAGER = 'MANAGER',
  L2MANAGER = 'L2MANAGER',
  L3MANAGER = 'L3MANAGER',
  LOCATION = 'LOCATION',
  DEPARTMENT = 'DEPARTMENT',
  DIVISION = 'DIVISION',
  PRODUCT = 'PRODUCT',
  LAST_ALLOCATION = 'LAST_ALLOCATION',
  PERFORMANCE = 'PERFORMANCE',
  SUMMARY = 'SUMMARY',
  MATCHING = 'MATCHING',
  MISSING = 'MISSING',
  INDIVIDUAL_CRITICALITY = 'INDIVIDUAL_CRITICALITY',
}

export const PEOPLE_COMPARISON_COLUMN_FIELDS = {
  [PEOPLE_COMPARISON_COLUMN_KEYS.CONFIRMED_SKILLS]: 'Confirmed Skills',
  [PEOPLE_COMPARISON_COLUMN_KEYS.INFERRED_SKILLS]: 'Inferred Skills',
  [PEOPLE_COMPARISON_COLUMN_KEYS.MISSING_SKILLS]: 'Missing Skills',
  [PEOPLE_COMPARISON_COLUMN_KEYS.MANAGER]: 'Manager',
  [PEOPLE_COMPARISON_COLUMN_KEYS.L2MANAGER]: 'L2 Manager',
  [PEOPLE_COMPARISON_COLUMN_KEYS.L3MANAGER]: 'L3 Manager',
  [PEOPLE_COMPARISON_COLUMN_KEYS.LOCATION]: 'Location',
  [PEOPLE_COMPARISON_COLUMN_KEYS.DEPARTMENT]: 'Department',
  [PEOPLE_COMPARISON_COLUMN_KEYS.DIVISION]: 'Division',
  [PEOPLE_COMPARISON_COLUMN_KEYS.PRODUCT]: 'Product',
  [PEOPLE_COMPARISON_COLUMN_KEYS.LAST_ALLOCATION]: (days: number) =>
    `Recent Activity Criticality (Last ${days}d)`,
  [PEOPLE_COMPARISON_COLUMN_KEYS.INDIVIDUAL_CRITICALITY]: 'Individual Criticality',
  [PEOPLE_COMPARISON_COLUMN_KEYS.PERFORMANCE]: 'Performance',
  [PEOPLE_COMPARISON_COLUMN_KEYS.SUMMARY]: 'Summary',
  [PEOPLE_COMPARISON_COLUMN_KEYS.MATCHING]: 'Matching',
  [PEOPLE_COMPARISON_COLUMN_KEYS.MISSING]: 'Missing',
} as const;

export const FeedbackFormSchema = () => {
  return yup.object().shape({
    feedback: yup.string().label('feedback').required('Feedback is required'),
  });
};

export const getUpdatedSkillType = (type: string) => {
  if (type in SkillType) {
    return SkillType[type as SkillTypeKey];
  }
  return '';
};

export const getSkillGroups = (profileSkills: any[], requiredSkills: Skill[], evidenceMatchedIds: string[] = []) => {
  const profileSkillsById = new Map(profileSkills.map((skill) => [skill.id, skill]));
  const evidenceMatchedIdSet = new Set(evidenceMatchedIds);

  const verifiedSkills: Skill[] = [];
  const inferredSkills: Skill[] = [];
  const missingSkills: Skill[] = [];

  for (const requiredSkill of requiredSkills) {
    const profileSkill = profileSkillsById.get(requiredSkill.id);

    if (!profileSkill && evidenceMatchedIdSet.has(requiredSkill.id)) {
      inferredSkills.push(requiredSkill);
    } else if (!profileSkill) {
      missingSkills.push(requiredSkill);
    } else if (profileSkill.isVerified) {
      verifiedSkills.push(profileSkill);
    } else {
      inferredSkills.push(profileSkill);
    }
  }

  return {
    verifiedSkills,
    inferredSkills,
    missingSkills,
  };
};

export const renderWithCriticalityBadge = (personData: any): React.ReactNode => {
  const isCritical =
    (personData.organizationProfile?.recentActivitiesCriticalityMetadata
      ?.highCriticalityActivitiesCount ?? 0) > 0 ||
    (personData.allocationMetadata?.highCriticalityActivitiesCount ?? 0) > 0;

  return (
    <Box display="flex" alignItems="center" gap={3}>
      {isCritical ? (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography fontSize="12px" fontWeight="400">
            Critical
          </Typography>
          <CircleIcon sx={{ color: colorPalette.RED400, fontSize: 12 }} />
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography fontSize="12px" fontWeight={'400'}>
            Non-Critical
          </Typography>
          <CircleIcon sx={{ color: colorPalette.GREEN400, fontSize: 12 }} />
        </Box>
      )}
    </Box>
  );
};

export type AddFeedbackParams = {
  revieweeId: string;
  skillIds: string[];
  feedback: string;
};

export type FeedbackForm = {
  feedback: string;
};

export type PastFeedback = {
  id: string;
  feedback: string;
  createdDate: string;
  skills: Skill[];
};

export enum MatchingEngineContributorDetailDrawer {
  LastAllocation = 'last-allocation',
  TrackRecord = 'track-record',
  Feedback = 'feedback',
  Skills = 'skills',
}

export enum VoteType {
  UPVOTE = 'UPVOTE',
  DOWNVOTE = 'DOWNVOTE',
}

export const RoundedEdgeCard: React.FC<{
  isFirstCard?: boolean;
  isLastCard?: boolean;
  elevation?: number;
  children: React.ReactNode;
}> = ({ isFirstCard = true, isLastCard = true, elevation = 0, children }) => {
  const { palette } = useTheme();
  const borderColor = palette.Buttons.Label.LabelDisabled;
  return (
    <Card
      elevation={elevation}
      sx={{
        borderRadius: 0,
        borderTopLeftRadius: isFirstCard ? 12 : 0,
        borderTopRightRadius: isFirstCard ? 12 : 0,
        borderBottomLeftRadius: isLastCard ? 12 : 0,
        borderBottomRightRadius: isLastCard ? 12 : 0,
        border: `1px solid ${borderColor}`,
      }}
    >
      {children}
    </Card>
  );
};

export const MATCHING_ENGINE_PERSON_DRAWER_ID = DrawerIdEnum.MATCHING_ENGINE_PERSON;

type NoMatchComponentProps = {
  title?: string;
  subText?: string;
  dataAnalyticsTag?: string;
};

export const NoMatchFound: React.FC<NoMatchComponentProps> = ({
  title = 'Nothing matched!',
  subText = 'Try refining your search.',
  dataAnalyticsTag = 'search_no_results_found',
}) => {
  const { palette } = useTheme();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2.5}
      pt={4.5}
      data-analytics-tag={dataAnalyticsTag}
    >
      <Stack spacing={1.5} alignItems="center" justifyContent="center">
        <NoResultsImage />
        <Typography variant="body2" fontWeight={500} color={palette.Text.Headline}>
          {title}
        </Typography>
        <Typography variant="body3" color={palette.Text.SubHeadline} textAlign="center">
          {subText}
        </Typography>
      </Stack>
    </Stack>
  );
};

export function extractPersonSummary(row: any) {
  return {
    name: row.name,
    photo: row.photo,
    profileID: row.profileID,
    feedbacks: row.feedbacks,
    skillID: row.skillID,
  };
}

export const findSkillsSortedByRarity = (
  searchedSkills: { id: string; name: string }[],
  people: any[],
) => {
  if (searchedSkills.length <= 1) return searchedSkills;
  const skillFrequencyMap: Record<string, number> = {};

  for (const person of people) {
    const personSkillIds = new Set(person.skills.map((s: { id: string }) => s.id));
    for (const skill of searchedSkills) {
      if (!personSkillIds.has(skill.id)) continue;
      skillFrequencyMap[skill.id] = (skillFrequencyMap[skill.id] || 0) + 1;
    }
  }

  const sortedByNiche = [...searchedSkills].sort((a, b) => {
    const countA = skillFrequencyMap[a.id] || 0;
    const countB = skillFrequencyMap[b.id] || 0;
    return countA - countB; // Ascending: least common first
  });

  return sortedByNiche;
};

type GridCellItemProps = {
  value: any;
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  readonly?: boolean;
  alignCenter?: boolean;
  sx?: SxProps;
};

export const GridCellItem: React.FC<GridCellItemProps> = ({
  value,
  onClick,
  readonly = false,
  alignCenter = false,
  sx,
}) => {
  const { palette } = useTheme();

  if (_.isNil(value)) return null;

  return (
    <Stack
      alignItems={alignCenter ? 'center' : 'flex-start'}
      justifyContent="center"
      sx={{
        ...sx,
        width: '100%',
        height: '100%',
        cursor: readonly ? 'default' : 'pointer',
        '&:hover': readonly
          ? undefined
          : {
              backgroundColor: palette.action.hover,
            },
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (readonly) return;
        onClick?.(e);
      }}
    >
      <Typography
        variant="body4"
        sx={{
          color: palette.LinkText.Default,
          fontWeight: 500,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

export const getLastAllocationsTooltip = (currentAllocations: any, children: any) => {
  const tooltipTitle = (
    <Stack spacing={3}>
      {currentAllocations.map(
        (workItem: {
          id: string;
          name: string;
          lastContributionDate: string;
          sponsor: string;
          source: string;
        }) => (
          <Stack key={workItem.id} spacing={0.1}>
            <Typography variant="body4" fontWeight={700}>
              {workItem.name}
            </Typography>
            <Typography variant="body4">
              Last Contribution:{' '}
              {moment.utc(workItem.lastContributionDate, 'YYYY-MM-DD').format('DD MMM yyyy')}
            </Typography>
            <Typography variant="body4">Sponsor: {workItem.sponsor}</Typography>
            <Typography variant="body4">Source: {workItem.source}</Typography>
          </Stack>
        ),
      )}
    </Stack>
  );

  return (
    <Tooltip title={tooltipTitle}>
      <Typography
        variant="body4"
        sx={{
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',

          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '4px',
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};

export const PerformanceRankLabels: Record<number, string> = {
  0: 'UNKNOWN',
  1: 'TOP 10%',
  2: 'TOP 30% (Next 20%)',
  3: 'MIDDLE 40%',
  4: 'BOTTOM 30% (Next 20%)',
  5: 'BOTTOM 10%',
};

export const getPerformanceTooltip = (
  performance: any,
  row: any,
  clickHandler: ClickHandler,
) => {
  if (!performance?.profileId) return '';

  const tooltipTitle = (
    <Stack spacing={0.75}>
      <Typography variant="body4">
        How likely is {row.name} to be recommended to a <b>customer or stakeholder</b> to have
        on their project?:{' '}
        {performance.customerRecommendationScore != null
          ? `${performance.customerRecommendationScore}/10`
          : '-'}
      </Typography>

      <Typography variant="body4">
        How likely is {row.name} to be recommended to a <b>colleague</b> to have on their
        team?:{' '}
        {performance.peerRecommendationScore != null
          ? `${performance.peerRecommendationScore}/10`
          : '-'}
      </Typography>

      <Typography variant="body4">
        How likely is {row.name} to be recommended to a <b>manager</b> to lead a project or
        initiative?:{' '}
        {performance.managerRecommendationScore != null
          ? `${performance.managerRecommendationScore}/10`
          : '-'}
      </Typography>

      <Typography variant="body4">
        Ratings Reason: {performance.recommendationComments || '-'}
      </Typography>
      <Typography variant="body4">
        Performance Rating:{' '}
        {performance.performanceRating != null ? `${performance.performanceRating}/5` : '-'}
      </Typography>
      <Typography variant="body4">
        Performance Reason: {performance.performanceReason || '-'}
      </Typography>
      <Typography variant="body4">
        Potential Rating:{' '}
        {performance.potentialRating != null ? `${performance.potentialRating}/5` : '-'}
      </Typography>
      <Typography variant="body4">
        Potential Reason: {performance.potentialReason || '-'}
      </Typography>
      <Typography variant="body4">
        Criticality Rating:{' '}
        {performance.criticalityRating != null ? `${performance.criticalityRating}/5` : '-'}
      </Typography>
      <Typography variant="body4">
        Criticality Reason: {performance.criticalityReason || '-'}
      </Typography>
    </Stack>
  );

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <GridCellItem
          readonly={false}
          value={PerformanceRankLabels[performance.rank]}
          alignCenter={true}
          onClick={clickHandler}
        />
      </span>
    </Tooltip>
  );
};

export const renderCurrentAllocationDetailsGridCell = (
  personData: any,
  clickHandler: ClickHandler,
) => {
  const currentAllocations =
    personData.allocationMetadata ??
    personData.organizationProfile?.recentActivitiesCriticalityMetadata;

  if (!currentAllocations) {
    return (
      <GridCellItem
        readonly={false}
        value={
          <Typography
            variant="caption"
            fontStyle={'italic'}
            color={ColorPalette.DISABLED_TEXT}
          >
            Unknown
          </Typography>
        }
        onClick={clickHandler}
      />
    );
  }

  return (
    <GridCellItem
      readonly={false}
      value={renderWithCriticalityBadge(personData)}
      onClick={clickHandler}
    />
  );
};

const RecentActivityHeader = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isCritical = searchParams.has(CRITICAL_PEOPLE_SEARCH_PARAM);

  const handleToggle = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);

    if (isCritical) {
      updatedSearchParams.delete(CRITICAL_PEOPLE_SEARCH_PARAM);
    } else {
      updatedSearchParams.set(CRITICAL_PEOPLE_SEARCH_PARAM, 'true');
    }

    setSearchParams(updatedSearchParams);
  };

  return (
    <Box display="flex" alignItems="center" gap={6}>
      <Typography
        variant="h4"
        fontSize="0.725rem"
      >{`Recent Activity Criticality (Last ${CUT_OFF_DAY_COUNT}d)`}</Typography>

      <FormControlLabel
        sx={{
          '& .MuiFormControlLabel-label': {
            lineHeight: '0px',
          },
        }}
        control={
          <Switch
            checked={isCritical}
            onChange={handleToggle}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        }
        label={
          <Typography variant="h4" fontSize="0.725rem">
            Non-Critical Activity Only
          </Typography>
        }
        labelPlacement="end"
      />
    </Box>
  );
};

const getActivityNameColumn = (
  openDrawer: Function,
  isSkillFilterGrid = false,
): GridColumn => ({
  field: 'activityName',
  headerName: `Recent Activity Criticality (Last ${CUT_OFF_DAY_COUNT}d)`,
  width: 425,
  minWidth: 175,
  renderCell: ({ row }) => {
    const clickHandler = openMatchingEnginePersonDrawer(
      row,
      MatchingEngineContributorDetailDrawer.LastAllocation,
      openDrawer,
      isSkillFilterGrid,
    );
    return renderCurrentAllocationDetailsGridCell(row, clickHandler);
  },
  renderHeader: () => <RecentActivityHeader />,
  valueGetter: (value, row) => {
    return get(row, 'organizationProfile.recentActivitiesCriticalityMetadata');
  },
});

export const usePerformanceRankColumn = (openDrawer: Function): GridColumn[] => {
  const featureAccess = useFeatureAccess(FeaturesEnum.PERFORMANCE_RANK);
  const hasAccess = !!featureAccess[FeatureAccessEnum.READ];

  if (!hasAccess) return [];

  return [
    {
      field: 'performanceRank',
      headerName: 'Performance Rank',
      width: 200,
      renderCell: ({ row }) => {
        const clickHandler = openMatchingEnginePersonDrawer(
          row,
          MatchingEngineContributorDetailDrawer.Feedback,
          openDrawer,
        );
        return row.performance?.profileId ? (
          <GridCellItem
            readonly={false}
            value={PerformanceRankLabels[row.performance?.rank]}
            alignCenter={true}
            onClick={clickHandler}
          />
        ) : null;
      },
      valueGetter: (_, row) => get(row, 'performance.rank'),
    },
    {
      field: 'individualCriticality',
      headerName: 'Individual Criticality',
      width: 125,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={true}
            value={
              row.performance?.criticalityRating
                ? `${row.performance.criticalityRating}/5`
                : '-'
            }
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'individualCriticality');
      },
    },
  ];
};

export const usePeopleGridColumns = (): GridColumn[] | [] => {
  const { openDrawer } = useDrawer();
  return [
    {
      field: 'name',
      headerName: 'Name',
      width: 140,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.profileID}
                id={row.profileID}
                name={row.name}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'name');
      },
    },
    getActivityNameColumn(openDrawer),
    getStaticLinkColumn({
      field: 'department',
      headerName: 'Department',
      staticDataType: 'DEPARTMENT',
    }),
    {
      field: 'joinDate',
      headerName: 'Join Date',
      width: 125,
      renderCell: ({ row }) => {
        const rawDate = row.joinDate;
        const formattedDate = rawDate
          ? moment.utc(rawDate, 'YYYY-MM-DD').format('DD MMM yyyy')
          : '';

        return <GridCellItem readonly value={formattedDate} />;
      },
      valueGetter: (value, row) => {
        return get(row, 'joinDate');
      },
    },
    {
      field: 'level',
      headerName: 'Level',
      width: 50,
      renderCell: ({ row }) => {
        const level = row.level;
        return <GridCellItem readonly={true} value={level ? `L-${level}` : ''} />;
      },
      valueGetter: (value, row) => get(row, 'level', null),
    },
    {
      field: 'manager',
      headerName: 'Manager',
      width: 125,
      renderCell: ({ row }) => {
        return row.manager?.id ? (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.manager.id}
                id={row.manager.id}
                name={row.manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'manager.fullName');
      },
    },
    ...usePerformanceRankColumn(openDrawer),
    {
      field: 'employeeType',
      headerName: 'Employee Type',
      width: 200,
      renderCell: ({ row }) => {
        return <GridCellItem readonly={true} value={row.employeeType} />;
      },
      valueGetter: (value, row) => {
        return get(row, 'employeeType');
      },
    },
    {
      field: 'l2manager',
      headerName: 'L2 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l2Manager?.id ? (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.l2Manager.id}
                id={row.l2Manager.id}
                name={row.l2Manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l2Manager.fullName');
      },
    },
    {
      field: 'l3manager',
      headerName: 'L3 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l3Manager?.id ? (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.l3Manager.fullName}
                id={row.l3Manager.id}
                name={row.l3Manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l3Manager.fullName');
      },
    },
    {
      field: 'gridActions',
      headerClassName: 'gridActions',
      renderHeader: () => <CXDataGridManageColumnsButton />,
      width: 40,
      minWidth: 40,
      sortable: false,
      resizable: false,
      pinnable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ];
};

type StaticLinkColumnOptions = {
  field: string;
  headerName: string;
  width?: number;
  staticDataType: string;
};

export const getStaticLinkColumn = ({
  field,
  headerName,
  width = 200,
  staticDataType,
}: StaticLinkColumnOptions): GridColDef => ({
  field,
  headerName,
  width,
  renderCell: ({ row }) => {
    const entity = row[field];

    if (!entity) return '';

    return (
      <GridCellItem
        readonly={true}
        value={
          <CommonStyledLink
            color="inherit"
            sx={{ fontWeight: 500 }}
            href={AppRoutesEnum.STATIC_DATA_SUMMARY}
            params={{ staticDataType, staticDataId: `${entity.id}` }}
            variant="body4"
          >
            {entity.name}
          </CommonStyledLink>
        }
      />
    );
  },
  valueGetter: (value, row) => get(row, `${field}.name`),
});

export const openMatchingEnginePersonDrawer =
  (
    row: any,
    initialTab: MatchingEngineContributorDetailDrawer,
    openDrawer: any,
    shouldShowSkillTab?: boolean,
  ) =>
  (e: React.MouseEvent) => {
    e.stopPropagation();
    openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
      person: extractPersonSummary(row),
      initialTab,
      skillGroups: {
        managerVerifiedSkills: row.verifiedPersonSkills,
        inferredSkills: row.inferredSkills,
        missingSkills: row.missingSkills,
      },
      shouldShowSkillTab,
    });
  };

export const useSkillFilterGridColumns = (): GridColumn[] => {
  const { openDrawer } = useDrawer();
  return [
    {
      field: 'name',
      headerName: 'Name',
      width: 140,
      renderCell: ({ row }) => {
        return (
          <PersonLink
            key={row.profileID}
            id={row.profileID}
            name={row.name}
            color="inherit"
            fontWeight={500}
          />
        );
      },
    },
    getActivityNameColumn(openDrawer, true),
    getStaticLinkColumn({
      field: 'department',
      headerName: 'Department',
      staticDataType: 'DEPARTMENT',
    }),
    {
      field: 'matching',
      headerName: 'Matching',
      headerAlign: 'center',
      minWidth: 90,
      flex: 0,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={false}
            value={row.skillMatchSummary.matching}
            onClick={openMatchingEnginePersonDrawer(
              row,
              MatchingEngineContributorDetailDrawer.Skills,
              openDrawer,
              true,
            )}
            alignCenter={true}
          />
        );
      },
      renderHeader: () => (
        <Tooltip title="Skills confirmed by Manager or Inferred from Work History" arrow>
          <Box display="flex" alignItems="center" gap={0.5}>
            Matching
          </Box>
        </Tooltip>
      ),
      valueGetter: (value, row) => {
        return get(row, 'skillMatchSummary.matching');
      },
    },
    ...usePerformanceRankColumn(openDrawer),
    {
      field: 'manager',
      headerName: 'Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.manager?.id ? (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.manager.id}
                id={row.manager.id}
                name={row.manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'manager.fullName');
      },
    },
    {
      field: 'l2manager',
      headerName: 'L2 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l2Manager?.id ? (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.l2Manager.id}
                id={row.l2Manager.id}
                name={row.l2Manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l2Manager.fullName');
      },
    },
    {
      field: 'l3manager',
      headerName: 'L3 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l3Manager?.id ? (
          <GridCellItem
            readonly={true}
            value={
              <PersonLink
                key={row.l3Manager.fullName}
                id={row.l3Manager.id}
                name={row.l3Manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l3Manager.fullName');
      },
    },
    getStaticLinkColumn({
      field: 'location',
      headerName: 'Location',
      staticDataType: 'WORK_LOCATION',
    }),
    getStaticLinkColumn({
      field: 'division',
      headerName: 'Division',
      staticDataType: 'DIVISION',
    }),
    getStaticLinkColumn({
      field: 'product',
      headerName: 'Product',
      staticDataType: 'PRODUCT',
    }),
    {
      field: 'gridActions',
      headerClassName: 'gridActions',
      renderHeader: () => <CXDataGridManageColumnsButton />,
      // This is required to make the cells under the grid action toolbar not act as checkboxes.
      renderCell: () => {
        return <GridCellItem readonly={true} value={' '} />;
      },
      width: 40,
      minWidth: 40,
      sortable: false,
      resizable: false,
      pinnable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ];
};

export const getDateLabel = (date: Date) => {
  const todayDate = moment.utc().startOf('day');
  const days = moment.duration(todayDate.diff(moment.utc(date).startOf('day'))).asDays();

  if (days === 0) {
    return 'Today';
  }
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

export const FeedbackList: React.FC<{
  feedbacks: any[];
  showVoteAndDeleteButtons?: boolean;
}> = ({ feedbacks, showVoteAndDeleteButtons = true }) => {
  if (!feedbacks || feedbacks.length === 0)
    return (
      <Typography fontStyle={'italic'} variant={'body3'}>
        No Feedbacks found
      </Typography>
    );
  return (
    <Stack gap={1} mt={2} width="100%">
      <ToggleableList
        items={feedbacks}
        text={'No Feedbacks Found'}
        renderItem={(item) => (
          <FeedbackCard
            key={item.id}
            feedback={item}
            showVoteAndDeleteButtons={showVoteAndDeleteButtons}
          />
        )}
      />
    </Stack>
  );
};

type ToggleableListProps = {
  items: any[];
  initialVisibleCount?: number;
  renderItem: (item: any) => React.ReactNode;
  text?: string;
};

export const ToggleableList: React.FC<ToggleableListProps> = ({
  items,
  initialVisibleCount = 3,
  renderItem,
  text = 'No Items Found',
}) => {
  const [visibleCounts, setVisibleCounts] = useState(initialVisibleCount);

  const itemCount = items?.length ?? 0;

  if (itemCount === 0) {
    return (
      <Typography fontStyle="italic" fontSize="15px">
        {text}
      </Typography>
    );
  }

  const showMore = () => {
    setVisibleCounts((prev) => Math.min(prev + 3, itemCount));
  };

  const showLess = () => {
    setVisibleCounts(initialVisibleCount);
  };

  return (
    <Stack spacing={1}>
      {items.slice(0, visibleCounts).map((item) => renderItem(item))}

      <Stack alignItems={'flex-start'}>
        {visibleCounts < itemCount && (
          <Button size="small" onClick={showMore}>
            See More
          </Button>
        )}

        {itemCount > initialVisibleCount && visibleCounts >= itemCount && (
          <Button onClick={showLess}>See Less</Button>
        )}
      </Stack>
    </Stack>
  );
};

export const FeedbackCard: React.FC<{
  feedback: any;
  showVoteAndDeleteButtons?: boolean;
}> = ({ feedback, showVoteAndDeleteButtons }) => {
  const { palette } = useTheme();
  const { account: loggedInUser } = useSession();

  const {
    id,
    skills,
    createdDate,
    reviewer,
    feedback: feedbackText,
    skillId,
    voteType,
    revieweeId,
  } = feedback;

  const headers = [`${reviewer.fullName}`, `${getDateLabel(createdDate)}`];

  const isManagerOfReviewee = reviewer.id === loggedInUser.profileId;
  return (
    <Card noElevation sx={{ ml: 1 }}>
      <CardContent sx={{ backgroundColor: palette.Background, py: '8px !important' }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction={'row'} justifyContent={'flex-start'} spacing={0.5}>
            <VerifiedSharpIcon fontSize="inherit" color="primary" />
            <Typography variant="caption" color={palette.Text.SubHeadline}>
              {headers.join(' • ')}
            </Typography>
          </Stack>

          {showVoteAndDeleteButtons && isManagerOfReviewee && (
            <PastFeedbackDropdownMenu
              profileId={revieweeId}
              feedbackId={id}
              skillId={skillId}
            />
          )}
        </Stack>

        <Stack flexDirection="row" mt={'1px'}>
          {showVoteAndDeleteButtons && (
            <>
              {voteType === 'UPVOTE' && <UpvoteButton isSelected />}
              {voteType === 'DOWNVOTE' && <DownvoteButton isSelected />}
            </>
          )}

          <Box sx={{ width: '95%' }} ml={'7px'}>
            <CXViewer value={feedbackText} isInitiallyExpanded={true} />
          </Box>
        </Stack>
        {skills && (
          <HorizontalScrollableContainer
            scrolledListElement={
              <Stack direction="row" gap={2}>
                {skills.map((skill: Skill) => (
                  <SkillChip key={skill.id} skill={skill} readonly={true} />
                ))}
              </Stack>
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export const FEEDBACK_MENU_OPTIONS: FloatingMenuItem[] = [
  {
    value: 'delete',
    label: 'Delete',
    palettePath: 'Failure',
  },
];
